import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface ImageModalProps {
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({
  images,
  currentIndex,
  onClose,
  onNext,
  onPrev,
}) => {
  const [currentImage, setCurrentImage] = useState(images[currentIndex]);

  useEffect(() => {
    setCurrentImage(images[currentIndex]);
  }, [currentIndex, images]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'ArrowRight') {
      onNext();
    } else if (event.key === 'ArrowLeft') {
      onPrev();
    } else if (event.key === 'Escape') {
      onClose();
    }
  }, [onNext, onPrev, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
      <div className="relative max-w-4xl w-full h-full flex items-center justify-center">
        <button
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
          onClick={onClose}
          aria-label="Close"
        >
          <XMarkIcon className="h-8 w-8" />
        </button>

        <img
          src={currentImage}
          alt={`Property image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
        />

        {images.length > 1 && (
          <>
            <button
              className="absolute left-4 text-white hover:text-gray-300 transition-colors duration-200 p-2 rounded-full bg-black bg-opacity-50"
              onClick={onPrev}
              aria-label="Previous image"
            >
              <ChevronLeftIcon className="h-10 w-10" />
            </button>
            <button
              className="absolute right-4 text-white hover:text-gray-300 transition-colors duration-200 p-2 rounded-full bg-black bg-opacity-50"
              onClick={onNext}
              aria-label="Next image"
            >
              <ChevronRightIcon className="h-10 w-10" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ImageModal;